import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { getNostrPool } from '../lib/nostrPool'
import { MODERATION_RELAYS, ADMIN_PUBKEYS } from '../config/constants'
import { Event } from 'nostr-tools'
import { fetchUserList } from '../services/listService'

interface ModerationContextType {
    blockedEventIds: Set<string>
    blockedPubkeys: Set<string>
    isLoading: boolean
}

const ModerationContext = createContext<ModerationContextType | undefined>(undefined)

export const ModerationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [blockedEventIds, setBlockedEventIds] = useState<Set<string>>(new Set())
    const [blockedPubkeys, setBlockedPubkeys] = useState<Set<string>>(new Set())
    const [isLoading, setIsLoading] = useState(true)

    // Memoized processor to handle incoming events from any source
    const processModerationEvent = useCallback((event: Event) => {
        const newBlockedEvents = new Set<string>()
        const newBlockedPubkeys = new Set<string>()

        event.tags.forEach(tag => {
            if (tag[0] === 'e') newBlockedEvents.add(tag[1])
            if (tag[0] === 'p') newBlockedPubkeys.add(tag[1])
        })

        if (newBlockedEvents.size > 0) {
            setBlockedEventIds(prev => {
                const next = new Set(prev)
                newBlockedEvents.forEach(id => next.add(id))
                return next
            })
        }
        if (newBlockedPubkeys.size > 0) {
            setBlockedPubkeys(prev => {
                const next = new Set(prev)
                newBlockedPubkeys.forEach(pk => next.add(pk))
                return next
            })
        }
    }, [])

    useEffect(() => {
        const pool = getNostrPool()
        console.log('[ModerationContext] Global sync started for admins:', ADMIN_PUBKEYS)

        const initialLoad = async () => {
            const allEventIds = new Set<string>()
            const allPubkeys = new Set<string>()

            // Parallel fetch for speed
            const listPromises = ADMIN_PUBKEYS.map(async (adminPk: string) => {
                try {
                    const list = await fetchUserList(adminPk, 'moderation')
                    if (list) {
                        list.items.forEach(i => {
                            if (i.type === 'event') allEventIds.add(i.value)
                            if (i.type === 'pubkey') allPubkeys.add(i.value)
                        })
                    }
                } catch (err) {
                    console.error(`[ModerationContext] Error loading admin ${adminPk}: `, err)
                }
            })

            await Promise.all(listPromises)

            if (allEventIds.size > 0) setBlockedEventIds(prev => new Set([...prev, ...allEventIds]))
            if (allPubkeys.size > 0) setBlockedPubkeys(prev => new Set([...prev, ...allPubkeys]))

            setIsLoading(false)
            console.log(`[ModerationContext] Initial load complete. Total: ${allEventIds.size}e, ${allPubkeys.size}p`)
        }

        initialLoad()

        // Persistent Live Subscription
        const filters = ADMIN_PUBKEYS.map((pubkey: string) => ({
            kinds: [30000],
            authors: [pubkey],
            '#d': ['moderation_log']
        }))

        const sub = pool.subscribeMany(MODERATION_RELAYS, filters, {
            onevent(event) {
                console.log(`[ModerationContext] LIVE update received: ${event.id.slice(0, 8)}`)
                processModerationEvent(event)
            }
        })

        return () => {
            console.log('[ModerationContext] Service terminated')
            sub.close()
        }
    }, [processModerationEvent])

    return (
        <ModerationContext.Provider value={{ blockedEventIds, blockedPubkeys, isLoading }}>
            {children}
        </ModerationContext.Provider>
    )
}

export const useModeration = () => {
    const context = useContext(ModerationContext)
    if (context === undefined) {
        throw new Error('useModeration must be used within a ModerationProvider')
    }
    return context
}
